'use client';

import { cn } from '@ahm/common-helpers';
import { useAuth } from '@ahm/react-auth';
import { useParams } from 'next/navigation';
import { useMemo, type HTMLAttributes, type ReactNode } from 'react';
import './booking-layout.css';
import BookingAside from './booking-aside';
import classes from './booking-layout.module.css';
import BookingNavbar from './booking-navbar';

import ChatInitializer from '@/components/chat-embed/chat-initializer';
import { ROUTES } from '@/config/router';
import { usePathname } from '@/navigation';

export type BookingLayoutProps = HTMLAttributes<HTMLElement> & {
  children: ReactNode;
  innerClassName?: string;
  withAsideMap?: boolean;
  withNavbar?: boolean;
};

export default function BookingLayout({
  children,
  className,
  innerClassName,
  withAsideMap,
  withNavbar,
  ...props
}: BookingLayoutProps) {
  const pathname = usePathname();
  const params = useParams();
  const auth = useAuth();

  const isWithNavbar = useMemo(() => {
    switch (pathname) {
      case ROUTES.lookback.driver:
      case ROUTES.lookback.driverStory:
      case ROUTES.lookback.user:
      case ROUTES.lookback.userStory:
      case ROUTES.payment.transactions:
      case ROUTES.payment.transactionDetail(params.id as string):
        // case ROUTES.subscription:
        // case ROUTES.subscriptionDetail(params.id as string):
        return false;

      default:
        return withNavbar;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, withNavbar]);

  const isWithAsideMap = useMemo(() => {
    switch (pathname) {
      case ROUTES.rewards:
      case ROUTES.rewardsByCategory:
      case ROUTES.lookback.driver:
      case ROUTES.lookback.driverStory:
      case ROUTES.lookback.user:
      case '/ro':
      case ROUTES.lookback.userStory:
      case ROUTES.payment.transactions:
      case ROUTES.payment.transactionDetail(params.id as string):
      case ROUTES.subscriptions:
      case ROUTES.subscriptionDetail(params.id as string):
      case ROUTES.mySubscriptionDetail(params.id as string):
      case ROUTES.pay(params.id as string):
      case ROUTES.checkout(params.type as string, params.id as string[]):
      case ROUTES.membership:
      case ROUTES.membershipHistory:
        return false;

      default:
        return withAsideMap;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, withAsideMap]);

  return (
    <main className={cn(className, classes.main)} {...props}>
      {isWithNavbar ? <BookingNavbar /> : null}

      {isWithAsideMap ? (
        <BookingAside className={cn(innerClassName)}>{children}</BookingAside>
      ) : (
        children
      )}

      {/* Chat Initializer */}
      {
        <>
          <ChatInitializer
            isAuthenticated={!!auth.session}
            userToken={auth.session?.token ?? null}
            theme="light"
          />
        </>
      }
    </main>
  );
}

export function BookingLayoutWithNavbar({
  children,
  className,
  ...props
}: Omit<BookingLayoutProps, 'withNavbar' | 'withAsideMap'>) {
  return (
    <main className={cn(className, classes.main)} {...props}>
      <BookingNavbar />
      {children}
    </main>
  );
}
